import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, CheckCircle2, XCircle, Loader2, RefreshCw, Home } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { apiFetch } from "@/lib/api";
import { useDiet } from "@/contexts/DietContext";
import { hasPendingDietPreferences } from "@/lib/pendingDietCheckout";

const POLL_INTERVAL_MS = 5000;
const MAX_POLL_MS = 10 * 60 * 1000; // 10 min

type Status = "pending" | "approved" | "rejected";

const PaymentPending = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshSubscription } = useDiet();

  const paymentId = searchParams.get("payment_id") || searchParams.get("collection_id");
  const externalReference = searchParams.get("external_reference");
  const mpStatus = searchParams.get("status") || searchParams.get("collection_status");
  const preferenceId = searchParams.get("preference_id");

  const [status, setStatus] = useState<Status>("pending");
  const [secondsToNext, setSecondsToNext] = useState(POLL_INTERVAL_MS / 1000);
  const [checking, setChecking] = useState(false);
  const [now] = useState(new Date());
  const startedAtRef = useRef(Date.now());
  const stoppedRef = useRef(false);

  // Redirect if no MP params at all (direct access)
  useEffect(() => {
    if (!paymentId && !externalReference && !preferenceId && !mpStatus) {
      navigate("/dashboard", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkStatus = async (): Promise<Status> => {
    setChecking(true);
    try {
      const res = await apiFetch<{ plan?: string; diet_credits?: number; subscription_status?: string }>(
        "api-subscription"
      );
      const isActive =
        (res?.plan && res.plan !== "free") ||
        (res?.diet_credits ?? 0) > 0 ||
        ["authorized", "active"].includes(res?.subscription_status ?? "");
      if (isActive) return "approved";
      return "pending";
    } catch {
      return "pending";
    } finally {
      setChecking(false);
    }
  };

  const handleManualCheck = async () => {
    const s = await checkStatus();
    if (s === "approved") {
      handleApproved();
    } else {
      toast.info("Pagamento ainda não confirmado. Continuamos verificando…");
    }
  };

  const handleApproved = async () => {
    if (stoppedRef.current) return;
    stoppedRef.current = true;
    setStatus("approved");
    await refreshSubscription();
    toast.success("Pagamento confirmado! 🎉");
    setTimeout(() => {
      if (hasPendingDietPreferences()) {
        navigate("/diet-builder?auto=true&payment=success", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    }, 3000);
  };

  // Auto polling
  useEffect(() => {
    if (mpStatus === "rejected" || mpStatus === "cancelled") {
      setStatus("rejected");
      stoppedRef.current = true;
      return;
    }

    let pollTimer: number | undefined;
    let countdownTimer: number | undefined;

    const tick = async () => {
      if (stoppedRef.current) return;
      if (Date.now() - startedAtRef.current > MAX_POLL_MS) {
        stoppedRef.current = true;
        return;
      }
      const s = await checkStatus();
      if (s === "approved") {
        handleApproved();
        return;
      }
      setSecondsToNext(POLL_INTERVAL_MS / 1000);
    };

    countdownTimer = window.setInterval(() => {
      setSecondsToNext((s) => (s > 1 ? s - 1 : POLL_INTERVAL_MS / 1000));
    }, 1000);

    pollTimer = window.setInterval(tick, POLL_INTERVAL_MS);

    return () => {
      if (pollTimer) clearInterval(pollTimer);
      if (countdownTimer) clearInterval(countdownTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isApproved = status === "approved";
  const isRejected = status === "rejected";

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="min-h-[70vh] flex items-center justify-center p-4"
    >
      <div className="w-full max-w-md bg-card rounded-2xl border border-border shadow-card p-6 sm:p-8 space-y-6 text-center">
        <div className="flex justify-center">
          <div
            className={`w-20 h-20 rounded-full flex items-center justify-center ${
              isApproved
                ? "bg-primary/10"
                : isRejected
                ? "bg-destructive/10"
                : "bg-yellow-500/10 animate-pulse"
            }`}
          >
            {isApproved ? (
              <CheckCircle2 className="w-10 h-10 text-primary" />
            ) : isRejected ? (
              <XCircle className="w-10 h-10 text-destructive" />
            ) : (
              <Clock className="w-10 h-10 text-yellow-600" />
            )}
          </div>
        </div>

        <div className="space-y-2">
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">
            {isApproved
              ? "Pagamento confirmado! 🎉"
              : isRejected
              ? "Pagamento não aprovado"
              : "Aguardando confirmação do pagamento"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isApproved
              ? "Redirecionando você para o sistema…"
              : isRejected
              ? "Não foi possível processar seu pagamento. Tente novamente."
              : "Seu PIX foi gerado. Assim que o pagamento for confirmado pelo banco, seu plano será ativado automaticamente."}
          </p>
        </div>

        <div className="rounded-xl border border-border bg-muted/30 p-4 text-left text-sm space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Status</span>
            {isApproved ? (
              <Badge className="bg-primary text-primary-foreground">Aprovado</Badge>
            ) : isRejected ? (
              <Badge variant="destructive">Rejeitado</Badge>
            ) : (
              <Badge className="bg-yellow-500/15 text-yellow-700 hover:bg-yellow-500/15 border border-yellow-500/30">
                Aguardando pagamento
              </Badge>
            )}
          </div>
          {externalReference && (
            <div className="flex items-start justify-between gap-3">
              <span className="text-muted-foreground">ID do pedido</span>
              <span className="font-mono text-xs text-foreground break-all text-right">
                {externalReference}
              </span>
            </div>
          )}
          {paymentId && (
            <div className="flex items-start justify-between gap-3">
              <span className="text-muted-foreground">ID do pagamento</span>
              <span className="font-mono text-xs text-foreground break-all text-right">
                {paymentId}
              </span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Tentativa</span>
            <span className="text-foreground">{now.toLocaleString("pt-BR")}</span>
          </div>
        </div>

        {!isApproved && !isRejected && (
          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Verificando novamente em {secondsToNext}s…
          </div>
        )}

        {!isApproved && !isRejected && (
          <p className="text-xs text-muted-foreground">
            Você já pode fechar esta tela. Quando o pagamento for confirmado, seu plano será ativado
            automaticamente.
          </p>
        )}

        <div className="space-y-2">
          {!isApproved && !isRejected && (
            <Button
              onClick={handleManualCheck}
              disabled={checking}
              className="w-full gradient-primary text-primary-foreground hover:opacity-90"
            >
              {checking ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verificando…
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" /> Verificar status
                </>
              )}
            </Button>
          )}
          <Button
            variant="outline"
            className="w-full"
            onClick={() => navigate(isRejected ? "/plans" : "/dashboard")}
          >
            <Home className="w-4 h-4 mr-2" />
            {isRejected ? "Voltar para planos" : "Ir para o início"}
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

export default PaymentPending;
